import { useMemo, useState } from 'react';
import { ModalDetalhe } from '../components/ModalDetalhe';
import { BuscaProcesso } from '../components/BuscaProcesso';
import { PageShell } from '../components/PageShell';
import { usePrazosVariasSemanas } from '../hooks';
import type { PrazoCard } from '../types';
import { PT_MES, semanaISO } from '../utils';

export function KanbanMensal() {
  const [mesRef, setMesRef] = useState<Date>(() => new Date());
  const [detalheId, setDetalheId] = useState<number | null>(null);

  const ano = mesRef.getFullYear();
  const mes = mesRef.getMonth();

  // Recua até o domingo anterior ao dia 1 (grid 6×7)
  const primeiroDia = useMemo(() => {
    const d = new Date(ano, mes, 1);
    d.setDate(1 - d.getDay());
    return d;
  }, [ano, mes]);

  // Calcula as semanas ISO únicas que cobrem o grid
  const semanas = useMemo(() => {
    const set = new Set<string>();
    for (let i = 0; i < 42; i++) {
      const d = new Date(primeiroDia);
      d.setDate(primeiroDia.getDate() + i);
      set.add(semanaISO(d));
    }
    return [...set];
  }, [primeiroDia]);

  const { data, isLoading } = usePrazosVariasSemanas(semanas);

  // Indexa cards por dia
  const porDia = useMemo(() => {
    const map = new Map<string, PrazoCard[]>();
    if (!data) return map;
    for (const semana of data) {
      for (const col of semana.colunas) {
        for (const c of col.cards) {
          const dia = c.data_evento ?? c.data_vencimento;
          if (!map.has(dia)) map.set(dia, []);
          map.get(dia)!.push(c);
        }
      }
    }
    return map;
  }, [data]);

  const hoje = new Date().toISOString().slice(0, 10);

  const totalNoMes = useMemo(() => {
    let n = 0;
    for (const [iso, cards] of porDia) {
      if (new Date(iso + 'T00:00:00').getMonth() === mes) n += cards.length;
    }
    return n;
  }, [porDia, mes]);

  function mudaMes(delta: number) {
    setMesRef(new Date(ano, mes + delta, 1));
  }

  /** Busca: pula para o MÊS do processo e abre o detalhe. */
  function irParaProcesso(card: PrazoCard) {
    const iso = card.data_evento ?? card.data_vencimento;
    const d = new Date(iso + 'T00:00:00');
    setMesRef(new Date(d.getFullYear(), d.getMonth(), 1));
    setDetalheId(card.id_prazo);
  }

  return (
    <PageShell
      subbarra={
        <>
          <div className="flex items-center gap-2">
            <button onClick={() => mudaMes(-1)} className="px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-300">‹</button>
            <button onClick={() => setMesRef(new Date())} className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 text-blue-700 dark:text-blue-300 rounded text-sm font-medium">Hoje</button>
            <button onClick={() => mudaMes(1)} className="px-3 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-300">›</button>
            <h2 className="ml-2 text-sm font-medium">{PT_MES[mes]} · {ano}</h2>
          </div>
          <div className="flex items-center gap-3">
            <BuscaProcesso onSelect={irParaProcesso} dica="Clique para ir até o mês do prazo" larguraInput="w-40 sm:w-52" />
            <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {isLoading ? 'carregando…' : `${totalNoMes} cards no mês`}
            </div>
          </div>
        </>
      }
    >
        <div className="glass-panel rounded-xl shadow-xl overflow-hidden">
          <div className="grid grid-cols-7 bg-white/10 dark:bg-slate-950/40 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((d, i) => (
              <div
                key={d}
                className={`px-2 py-2 text-center border-b border-slate-200/50 dark:border-slate-800/50 ${(i === 0 || i === 6) ? 'text-gray-400' : ''}`}
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 bg-white/5 dark:bg-slate-900/10">
            {Array.from({ length: 42 }).map((_, idx) => {
              const d = new Date(primeiroDia);
              d.setDate(primeiroDia.getDate() + idx);
              const iso = d.toISOString().slice(0, 10);
              const outroMes = d.getMonth() !== mes;
              const ehHoje = iso === hoje;
              const fds = d.getDay() === 0 || d.getDay() === 6;
              const cardsDoDia = porDia.get(iso) ?? [];
              const overflow = cardsDoDia.length > 3 ? cardsDoDia.length - 3 : 0;
              const mostrar = cardsDoDia.slice(0, 3);

              return (
                <div
                  key={iso}
                  className={`dia-cell p-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 ${ehHoje ? 'dia-cell-hoje' : ''} ${outroMes ? 'dia-cell-outro-mes' : ''}`}
                >
                  <div
                    className={`text-xs mb-1 ${ehHoje ? 'font-bold text-blue-700 dark:text-blue-300' : 'font-medium'} ${fds && !outroMes ? 'text-gray-400' : ''}`}
                  >
                    {ehHoje ? (
                      <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-600 text-white rounded-full text-[10px]">
                        {d.getDate()}
                      </span>
                    ) : (
                      d.getDate()
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {mostrar.map((c) => (
                      <div
                        key={c.id_prazo}
                        className={`mini-card e-${c.etiqueta}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetalheId(c.id_prazo);
                        }}
                        title={`${c.numero_processo} — ${c.tipo_prazo}`}
                      >
                        {c.numero_processo.slice(0, 13)}…
                      </div>
                    ))}
                    {overflow > 0 && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 pl-1">
                        + {overflow} mais
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Clique num card pra abrir o detalhe.
        </p>

      {detalheId !== null && (
        <ModalDetalhe idPrazo={detalheId} onClose={() => setDetalheId(null)} />
      )}
    </PageShell>
  );
}
